// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { type PropsWithChildren } from "react";

type GapSize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

/**
 * Static gap-size-to-pixel map matching antd's default design tokens.
 * These spacing values are theme-invariant (identical in light and dark mode),
 * so we avoid calling `theme.useToken()` on every render — eliminating a React
 * context subscription in 97+ consumer files.
 */
const GAP_SIZES: Record<GapSize, number> = {
  xxs: 4, // paddingXXS
  xs: 8, // paddingXS
  sm: 12, // paddingSM
  md: 20, // paddingMD
  lg: 24, // paddingLG
  xl: 32, // paddingXL
  xxl: 48, // sizeXXL (no paddingXXL alias in antd)
};

/**
 * Inline-style keys that have a typed `<Flex>` prop equivalent. `<Flex>`
 * emits these *after* `...style`, so setting them via `style` is dead code —
 * the typed prop always wins (see #3655). Banning them at the type level makes
 * `style={{ alignItems: ... }}` a compile error, steering callers to the prop.
 * The `fasttrack/no-flex-inline-layout-style` lint rule enforces the same
 * invariant in CI (which has no type-check gate) and autofixes common cases.
 */
type FlexManagedStyleKey =
  | "alignItems"
  | "justifyContent"
  | "flexDirection"
  | "flexWrap"
  | "flexShrink"
  | "gap";

export interface FlexProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "dir" | "style">,
    PropsWithChildren {
  direction?: "row" | "row-reverse" | "column" | "column-reverse";
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  justify?: "start" | "end" | "center" | "between" | "around";
  // | "evenly";
  align?: "start" | "end" | "center" | "baseline" | "stretch";
  gap?: number | GapSize;
  /**
   * Controls `flex-shrink`. Defaults to `0` to match historical behavior.
   *
   * Always emitted as an inline style (after `...style`) so it wins over
   * any className-based `flex` shorthand. Pass `shrink={1}` instead of
   * inlining `style={{ flexShrink: 1 }}` or (worse) `style={{ flex: 1 }}`
   * — the latter's `flex-shrink: 1` would silently lose to this inline
   * `flex-shrink: 0`. See issue #3655.
   */
  shrink?: number;
  /**
   * Standard inline styles, **minus** the layout keys `<Flex>` owns via typed
   * props (`align`, `justify`, `direction`, `wrap`, `shrink`, `gap`). Setting
   * one of those here is a compile error — use the prop instead.
   */
  style?: Omit<React.CSSProperties, FlexManagedStyleKey>;
}

const Flex: React.FC<FlexProps> = ({
  direction = "row",
  wrap = "nowrap",
  justify = "flex-start",
  align = "center",
  gap = 0,
  shrink = 0,
  style,
  children,
  ...restProps
}) => {
  const transferConst = [justify, align];
  const transferConstStyle = transferConst?.map((el) => {
    let tempTxt;
    switch (el) {
      case "start":
        tempTxt = "flex-start";
        break;
      case "end":
        tempTxt = "flex-end";
        break;
      case "between":
        tempTxt = "space-between";
        break;
      case "around":
        tempTxt = "space-around";
        break;
      default:
        tempTxt = el;
        break;
    }

    return tempTxt;
  });
  const flexStyle: React.CSSProperties = {
    // Layout defaults — overridable by caller `style`. These have no typed
    // prop equivalent on <Flex>, so callers setting them via `style`
    // (e.g. `position: "absolute"` for overlay badges, `minWidth: 160`)
    // must be allowed to win.
    display: "flex",
    boxSizing: "border-box",
    minHeight: 0,
    minWidth: 0,
    position: "relative",
    ...style,
    // Prop-derived style wins over caller `style` — prevents
    // `style.alignItems` from silently beating `align`, etc. See #3655.
    flexDirection: direction,
    flexWrap: wrap,
    justifyContent: transferConstStyle[0],
    alignItems: transferConstStyle[1],
    // Inlined (not in a className) so it beats any className-based `flex`
    // shorthand via CSS specificity. See #3655.
    flexShrink: shrink,
    gap: typeof gap === "string" ? GAP_SIZES[gap] : gap,
  };

  return (
    <div style={flexStyle} {...restProps}>
      {children}
    </div>
  );
};

export default Flex;
